import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/hooks/trpc";
import type { TestInstanceId } from "@brewbase/db/types";
import { useCapLabelStore } from "./cap-label-store";
import { capReader } from "./sample-number";

/** Mounted once at the root; opens whenever numbered tests were requested. */
export function CapLabelDialog() {
  const labels = useCapLabelStore((state) => state.labels);
  const close = useCapLabelStore((state) => state.close);
  const sorted = [...labels].sort((a, b) => a.id - b.id);
  // The assignee is read back from each request, wherever it was made.
  const details = trpc.useQueries((t) =>
    sorted.map((label) => t.tests.requests.detail(label.id as TestInstanceId)),
  );
  const assignees = sorted.map(
    (_, index) => details[index]?.data?.assignee_name,
  );
  const showAssignees = assignees.some(Boolean);

  return (
    <AlertDialog
      open={labels.length > 0}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <AlertDialogContent className="flex max-h-[85vh] flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">
            Label the cap{labels.length === 1 ? "" : "s"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Write {labels.length === 1 ? "this number" : "each number"} on the
            cap of the tube. {capReader(assignees)} finds the tube by it and
            enters it on the instrument.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* Bulk adds can name dozens of tubes: the list scrolls, the
            instruction and the button stay put. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {sorted.map((label) => (
                <tr key={label.id} className="border-b border-olive-4">
                  <td className="w-px whitespace-nowrap py-2 pr-6 font-mono text-readout font-semibold leading-none tabular-nums text-olive-12">
                    {label.id}
                  </td>
                  <td className="py-2 pr-4">
                    <span className="block text-olive-12">{label.sample}</span>
                    {label.sampleCode && (
                      <span className="block font-mono text-xs text-olive-9">
                        {label.sampleCode}
                      </span>
                    )}
                  </td>
                  <td className="w-px whitespace-nowrap py-2 text-olive-10">
                    {label.test}
                  </td>
                  {showAssignees && (
                    <td className="w-px whitespace-nowrap py-2 pl-4 text-olive-10">
                      {assignees[sorted.indexOf(label)] ?? "Unassigned"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AlertDialogFooter>
          <AlertDialogAction onClick={close}>Cap labelled</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
